import { useEffect, useMemo, useRef, useState } from "react";
import { competenciaParaMesAno } from "../utils/agregarPorConta.util";
import BotaoExportacaoProgresso, {
  rotuloDoEstado,
} from "../../../../components/BotaoExportacaoProgresso";
import styles from "../detalhe.styles.module.css";

const ITEM_MENU_STYLE = {
  display: "block",
  width: "100%",
  padding: "10px 14px",
  border: "none",
  background: "none",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 13,
};

const VARIANTES = [
  { tipo: "agrupado", rotulo: "Exportar Agrupado" },
  { tipo: "lancamentos", rotulo: "Exportar Lançamentos" },
  { tipo: "resumo_geral", rotulo: "Exportar Resumo Geral" },
];

/**
 * `progressos` — retorno de `useExportacaoAssincrona` por variante
 * ({ agrupado, lancamentos, resumo_geral }). Cada variante é um job independente no
 * servidor, então podem rodar juntas: o gatilho mostra o progresso da primeira ativa e o
 * menu continua abrindo pra disparar as outras; o item de uma variante em andamento fica
 * aria-disabled com o estado no próprio texto.
 */
export default function SectionToolbar({
  competenciaInicio,
  competenciaFim,
  totalLancamentos,
  totalContas,
  progressos = {},
  podeExportar = false,
  onExportAgrupado,
  onExportLancamentos,
  onExportResumoGeral,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const hintAgrupamento = useMemo(() => {
    const ini = competenciaParaMesAno(competenciaInicio);
    const fim = competenciaParaMesAno(competenciaFim);
    if (ini !== "—" && fim !== "—" && ini !== fim) {
      return `Agrupamento por conta e mês de competência (${ini} – ${fim})`;
    }
    if (ini !== "—") {
      return `Agrupamento por conta e mês de competência (${ini})`;
    }
    return "Agrupamento por conta e mês de competência no período filtrado";
  }, [competenciaInicio, competenciaFim]);

  const progressoGatilho = VARIANTES.map((v) => progressos[v.tipo]).find((p) => p?.ativo);

  const handleExport = (tipo) => {
    if (progressos[tipo]?.ativo) return;
    setMenuOpen(false);
    if (tipo === "agrupado") {
      onExportAgrupado?.();
    } else if (tipo === "resumo_geral") {
      onExportResumoGeral?.();
    } else {
      onExportLancamentos?.();
    }
  };

  return (
    <div className={styles.toolbar}>
      <span className={styles.toolbarHint}>
        {hintAgrupamento}
        {" · "}
        {totalLancamentos.toLocaleString("pt-BR")} lançamentos no resultado · {totalContas} linha
        {totalContas !== 1 ? "s" : ""} na lista
      </span>
      <div ref={menuWrapRef} style={{ position: "relative" }}>
        {podeExportar && (
          <BotaoExportacaoProgresso
            como="nativo"
            className={styles.btnExportar}
            progresso={progressoGatilho}
            rotulo="Exportar"
            bloquearDuranteJob={false}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          />
        )}
        {podeExportar && menuOpen && (
          <div
            role="menu"
            style={{
              position: "absolute",
              right: 0,
              top: "100%",
              marginTop: 4,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 10,
              minWidth: 200,
            }}
          >
            {VARIANTES.map(({ tipo, rotulo }) => {
              const p = progressos[tipo];
              const emAndamento = Boolean(p?.ativo);
              return (
                <button
                  key={tipo}
                  type="button"
                  role="menuitem"
                  aria-disabled={emAndamento || undefined}
                  style={{
                    ...ITEM_MENU_STYLE,
                    ...(emAndamento ? { cursor: "progress", color: "#666" } : null),
                  }}
                  onClick={() => handleExport(tipo)}
                >
                  {emAndamento ? `${rotulo} — ${rotuloDoEstado(p.estado, p.percentual, rotulo)}` : rotulo}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
